import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import LoggedInLayout from 'layouts/LoggedInLayout';
import useLeftMenu from 'hooks/useLeftMenu';
import { MENUS } from 'configs/page';
import useLookup from 'hooks/useLookup';
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import ButtonWithSpinner from 'components/buttons/ButtonSpinner';
import useCampaigns from 'hooks/useCampaigns';
import useToast from 'hooks/useToast';
import { PAGE_NAV } from 'configs/page';
import useAccount from 'hooks/useAccount';
import LinkToRHPAccountLink from 'components/LinkToRHPAccountLink';
import parse from 'html-react-parser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import ConfirmPopup from 'components/modals/ConfirmPopup';
import useModalDisplay from 'hooks/useDisplayModal';

const validationSchema = Yup.object().shape({
        title: Yup.string().trim().required("Campaign name is required"),
        landingPage: Yup.string().trim().required("Required"),
        code: Yup.string().max(200, 'Code must be no more than 200 characters')
    });

const AddCompaign = (props) => {
    const linkToRhpRef = useRef(null);
    const { rhpLinkAcc, rhpEvents, rhpGroups } = useAccount();
    const { history } = props;
    const [leftMenu, setLeftMenu] = useLeftMenu();
    const lookupProvider = useLookup();
    const { landingPages, getLandingPages } = lookupProvider;
    const mountedRef = useRef(true);
    const [fetchingLandingPages, setFetchingLandingPages] = useState(false);
    const { addNewCampaign } = useCampaigns();
    const toast = useToast();
    const {open, handleOpen, handleClose} = useModalDisplay();
    

    const {
        handleSubmit,
        setValue,
        trigger,
        clearErrors,
        formState: { errors },
        register,
    } = useForm({
        mode: "onSubmit",         
        reValidateMode: "onSubmit",
        defaultValues: { title: '', landingPage: '' },
        resolver: yupResolver(validationSchema),
    });

    const onSelectChange = (e)=>{
        if(e.target.value=="group" || e.target.value=="event"){
            setValue("landingPage", '');
            clearErrors("landingPage")
            linkToRhpRef && linkToRhpRef.current && linkToRhpRef.current.click();
            return;
        }
        setValue("landingPage", e.target.value);
        trigger("landingPage")
    }

    const onSubmitHandler = async (frmData, e) => {
        e.preventDefault();
        if (addNewCampaign.isLoading) return;

        addNewCampaign.mutateAsync({ 
            Title: frmData.title, 
            LandingPage: frmData.landingPage,
            Code: frmData.code
         })
            .then(newCampaign => {
                if (mountedRef.current)
                    history.push(`${PAGE_NAV.CAMPAIGNS}/${newCampaign.CampaignID}`);
            })
            .catch(err => toast.error("Failed to add new campaign", err));
    };

    useEffect(() => {
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        if (leftMenu.value !== "CAMPAIGNS" && mountedRef.current)
            setLeftMenu(MENUS.find(menu => menu.value === "CAMPAIGNS"));
    }, [leftMenu, setLeftMenu]);

    useEffect(() => {
        if (fetchingLandingPages) return;
        if (landingPages.length === 0) {
            setFetchingLandingPages(true);
            getLandingPages().finally(() => { mountedRef.current && setFetchingLandingPages(false); });
        }
    }, [landingPages, fetchingLandingPages, setFetchingLandingPages, getLandingPages]);
    const [showNote, setShowNote] = useState(false);
    return (
        <LoggedInLayout {...props} pageTitle="Add campaign">
            {/* <div className='mobile-page-header'>
                    <h1>Add campaign</h1>
            </div> */}
            <div className='card-group'>
            <div className={classNames("add-campaign", "card")}>
            
                <div className="card-content">
                    <form className="add-campaign-form" onSubmit={handleSubmit(onSubmitHandler)}>
                        <fieldset className="input">
                            <p className={classNames("name", { error: errors?.title?.message })}>
                                <span>
                                    <label htmlFor="campaign-name">Enter campaign name</label>
                                    <input id="campaign-name" type="text" placeholder="Enter campaign name" {...register("title")} />
                                    <span className="error-msg">{errors?.title?.message}</span>
                                </span>
                            </p>

                            <p className={classNames("name", { error: errors?.code?.message })}>
                                <span>
                                    <label className='tooltip' htmlFor="campaign-name">Enter promo code (optional) <span className='tooltip-ico' onMouseEnter= {()=>setShowNote(true)} onMouseLeave={()=>setShowNote(false)}><FontAwesomeIcon icon={faInfoCircle} />
                                    {
                                        showNote && <em className='tooltip-bubble'>Note: promo codes are valid for 30 days from creation</em>
                                      }</span></label>
                                    <input id="campaign-name" type="text" placeholder="Enter your promo code" {...register("code")} />
                                    
                                      
                                    
                                    <span className="error-msg">{errors?.code?.message}</span>
                                </span>
                            </p>

                            <div className={classNames("landing-page", { error: errors?.landingPage?.message })}>
                                <label htmlFor="landing-page">Landing page</label>
                                <div className='section-group'>
                                <span className="select">
                                    <select name="landing-page" id="landing-page" {...register("landingPage")} onChange={onSelectChange}>
                                        <option value="">Please select</option>
                                        {landingPages.map((page, i) => <option key={`lp_${i}`} value={page.TextAbbr}>{page.Text}</option>)}
                                        {
                                            !rhpLinkAcc?.Username &&
                                            <>
                                                <option value="group">Your group page</option>
                                                <option value="event">Your event page</option>
                                            </>
                                        }
                                        {rhpGroups?.length > 0 && <option disabled>-----Groups-----</option> }
                                        {rhpGroups.map((ev, i) => <option key={`grplp_${i}`} value={ev.TextAbbr}>Group - {parse(ev.Text)}</option>)}
                                        {rhpEvents?.length > 0 && <option disabled>-----Events-----</option> }
                                        {rhpEvents.map((ev, i) => <option key={`evlp_${i}`} value={ev.TextAbbr}>Event - {parse(ev.Text)}</option>)}
                                        
                                    </select>
                                </span>
                                {/* {
                                    !rhpLinkAcc?.Username && <LinkToRHPAccountLink cls="input-link">Link RHP Account</LinkToRHPAccountLink>
                                } */}
                                </div>
                                
                                <span className="error-msg">{errors?.landingPage?.message}</span>
                            </div>
                            
                        </fieldset>
                        <fieldset className="submit">
                            <p className="btn grey">
                                <ButtonWithSpinner text="Add campaign" isLoading={addNewCampaign.isLoading} />
                            </p>
                        </fieldset>
                    </form>
                    <LinkToRHPAccountLink 
                            ref={linkToRhpRef}
                            onSuccess={()=>{
                                handleOpen();
                            }}
                        ></LinkToRHPAccountLink>
                </div>
            </div>
            </div>
            <ConfirmPopup 
                open={open} 
                close={handleClose}
                confirm={handleClose} 
                heading="RHP Account linked" 
                btnText="OK">
                <p style={{textAlign:"center", lineHeight: "1.6", marginBottom: "25px"}}>Account successfully linked. <br/>Select your desired landing page</p>
            </ConfirmPopup>                      
        </LoggedInLayout>
    );
};

export default AddCompaign;
